import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HS_DEFAULT_VIEWER_FILTER,
  HsGraphicLayer,
  HsGraphicObject,
  HsGraphicQueue,
  HsMatrix4,
  HsPerspectiveCamera,
  HsQuaternion,
  HsRenderObjects,
  HsVector3,
  hsGenerateCubeGeometryData,
  hsMakeGeometry,
  type HsHypersceneViewer,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';

const AXIS: HsVector3 = new HsVector3(1, 0.5, 0.25).normalize();
const SCENE_ROTATION: HsMatrix4 = HsMatrix4.compose(
  [0, 0, 0],
  HsQuaternion.makeFromAxisAndAngle(AXIS.values, 0.01).values,
  [1, 1, 1]
);
const INSET_ROTATION: HsMatrix4 = HsMatrix4.compose(
  [0, 0, 0],
  HsQuaternion.makeFromAxisAndAngle(AXIS.values, 0.005).values,
  [1, 1, 1]
);
/** Share of the canvas the inset layer covers on each axis. */
const INSET_SCALE: number = 0.3;

/**
 * Two layers on one canvas: the scene layer fills the canvas, and a second layer with its own camera and cube
 * is drawn over the top left corner.
 */
@Component({
  selector: 'layer-position',
  templateUrl: './layer-position.component.html',
  styleUrls: ['./layer-position.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayerPositionDemo extends DemoViewerPage {
  private readonly insetCamera: HsPerspectiveCamera = new HsPerspectiveCamera(getUuid(), 'insetCamera');
  private sceneCube: HsGraphicObject | null = null;
  private insetCube: HsGraphicObject | null = null;

  protected initScene(viewer: HsHypersceneViewer): void {
    const geometry: HsRegularGeometry | null = hsMakeGeometry(viewer.surface.graphics, hsGenerateCubeGeometryData());
    if (geometry === null) {
      return;
    }
    this.sceneCube = new HsGraphicObject(getUuid(), 'sceneCube', geometry, makeLitMaterial(viewer.gl, 1, 0.5, 0.25));
    this.sceneCube.updateBoundingSphere();
    viewer.add(this.sceneCube);
    viewer.camera.worldMatrix.setPosition([0, 0, 5]);

    this.insetCube = new HsGraphicObject(getUuid(), 'insetCube', geometry, makeLitMaterial(viewer.gl, 1, 0.7, 0.1));
    this.insetCube.updateBoundingSphere();
    this.insetCamera.worldMatrix.setPosition([0, 0, 5]);

    const insetLayer: HsGraphicLayer = new HsGraphicLayer(this.insetCamera, viewer.gl, 4);
    // The viewer resizes overlay layers with the scene layer.
    viewer.overlayLayers.push(insetLayer);
    insetLayer.resizeFramebuffer(viewer.gl, viewer.gl.canvas.width, viewer.gl.canvas.height);
    const insetQueue: HsGraphicQueue = new HsGraphicQueue(getUuid(), insetLayer);
    insetQueue.addObject(this.insetCube);
    const insetObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer> = new HsRenderObjects(insetLayer);
    viewer.onFrame(() => this.drawInset(viewer, insetQueue, insetObjects));
  }

  protected override animate(): boolean {
    this.sceneCube?.worldMatrix.multiply(SCENE_ROTATION);
    this.insetCube?.worldMatrix.multiply(INSET_ROTATION);
    return this.sceneCube !== null;
  }

  /** Draws the inset layer over the frame the viewer just drew. */
  private drawInset(
    viewer: HsHypersceneViewer,
    queue: HsGraphicQueue,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>
  ): void {
    const width: number = viewer.gl.canvas.width;
    const height: number = viewer.gl.canvas.height;
    // The layer quad spans clip space -1..1 scaled by INSET_SCALE; the offset moves it into the top left corner.
    renderObjects.layer.setRect((INSET_SCALE - 1) * width, (1 - INSET_SCALE) * height, INSET_SCALE);
    this.insetCamera.aspect = viewer.camera.aspect;
    this.insetCamera.updateCameraMatrixInverse();
    this.insetCamera.updateProjectionMatrix();
    renderObjects.clear();
    HS_DEFAULT_VIEWER_FILTER.filter(queue, this.insetCamera, renderObjects);
    viewer.render.drawToFramebuffer(viewer.gl, this.insetCamera, renderObjects);
  }
}
